import {useMemo, useState, useEffect} from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {TablerPhone, TablerBolt} from '../../icons';
import {telephonicService} from '../../services/telephonicService';
import {patientService} from '../../services/patientService';
import {
  notifyPhoneCallPatientLink,
  subscribePhoneCallPatientLink,
} from '../planner/usePlannerPhoneCalls';
import {TELEPHONIC_COLOR} from '../../constants/designTokens';
import {toJsDate} from '../../services/encounterUtils';
import {RECAP_CARD_BOUNDARY_PROPS} from '../common/recapButtonStyles';
import SessionDetailView, {
  RecapSectionHeading,
} from '../common/SessionDetailView';
import EncounterDetailFrame from '../common/EncounterDetailFrame';
import ChangeSessionPatientModal from '../common/ChangeSessionPatientModal';
import {buildSessionOverviewItems} from '../common/sessionOverviewItems';
import MetaPill from '../common/MetaPill';
import {PLANNER_SIDEBAR_NAME_FONT_SIZE} from '../planner/PlannerSidebarList';
import {mapPhoneMessages} from './PhoneCallTranscript';

const PHONE_SUMMARY_PLACEHOLDERS = new Set(['Telephonic Interpretation']);

function cleanPhoneSummary(value: any) {
  const summary = String(value || '').trim();
  return PHONE_SUMMARY_PLACEHOLDERS.has(summary) ? '' : summary;
}

// Read-only detail view for a company-shared telephonic call. Renders through
// the shared SessionDetailView so it is visually identical to the regular
// encounter detail; the note panel is hidden (a phone call has no clinical
// note) and a "Create tel encounter" card occupies the clinical-note slot so
// the call can be turned into a telephone encounter.
const PhoneCallDetailPanel = ({
  call,
  onBack,
  onCreateTelephoneEncounter,
}: Record<string, any>) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const bodyText = useColorModeValue('gray.900', 'whiteAlpha.900');
  const sharedPillBg = useColorModeValue(
    `${TELEPHONIC_COLOR}1A`,
    `${TELEPHONIC_COLOR}26`,
  );
  const sharedPillBorder = useColorModeValue(
    `${TELEPHONIC_COLOR}33`,
    `${TELEPHONIC_COLOR}40`,
  );

  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const linkModal = useDisclosure();
  // Patient linkage for the call. Company phone calls arrive with no patient;
  // the registrar can link one (and unlink) just like a regular unlinked
  // encounter. Persistence is the backend's concern — held locally for now.
  const [linkedPatient, setLinkedPatient] = useState<any>(() =>
    call?.patient_id || call?.ehr_patient_id || call?.patient_name
      ? {
          patientId: call.patient_id || null,
          ehrPatientId: call.ehr_patient_id || null,
          patientName: call.patient_name || null,
        }
      : null,
  );

  useEffect(() => {
    if (!call?.id) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    telephonicService
      .getTelephonicConversation(call.id)
      .then(conv => {
        if (cancelled) return;
        setMessages(mapPhoneMessages(conv?.messages));
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[PhoneCallDetailPanel] failed to load transcript', err);
        setMessages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [call?.id]);

  // Keep the header live when the same conversation is linked from another
  // surface (e.g. the telephone-encounter detail's patient picker).
  useEffect(() => {
    if (!call?.id) return undefined;
    return subscribePhoneCallPatientLink((callId, patch) => {
      if (callId !== call.id) return;
      const hasPatient = Boolean(
        patch.patient_id || patch.ehr_patient_id || patch.patient_name,
      );
      setLinkedPatient(
        hasPatient
          ? {
              patientId: patch.patient_id || null,
              ehrPatientId: patch.ehr_patient_id || null,
              patientName: patch.patient_name || null,
            }
          : null,
      );
    });
  }, [call?.id]);

  const session = useMemo(() => {
    if (!call) return null;
    const minutes = Math.max(Math.round(call.minutes || 0), 1);
    const startJs = toJsDate(call.start_time);
    const endJs = call.end_time
      ? toJsDate(call.end_time)
      : startJs
        ? new Date(startJs.getTime() + minutes * 60_000)
        : null;
    return {
      id: call.id,
      start_time: call.start_time,
      end_time: endJs,
      minutes,
      summary: cleanPhoneSummary(call.summary) || 'Phone encounter',
      foreign_language: call.language || '',
    };
  }, [call]);

  if (!call || !session) return null;

  const handleCreate = async () => {
    if (!onCreateTelephoneEncounter || creating) return;
    setCreating(true);
    try {
      // Carry the registrar's current patient link (which may differ from the
      // raw call) so the open-TE check and the new draft both target it.
      await onCreateTelephoneEncounter({
        ...call,
        patient_id: linkedPatient?.patientId ?? call?.patient_id ?? null,
        ehr_patient_id:
          linkedPatient?.ehrPatientId ?? call?.ehr_patient_id ?? null,
        patient_name: linkedPatient?.patientName ?? call?.patient_name ?? null,
      });
    } finally {
      setCreating(false);
    }
  };

  // Link/unlink a patient on the company phone call. Reuses the shared patient
  // picker, persists to the conversation doc (so the link survives reload and
  // is shared across users), and broadcasts the change so the Recent list and
  // any telephone-encounter built from this call stay in sync in real time.
  const handleReassignPatient = async (payload: any) => {
    const hasPatient = Boolean(
      payload?.patientId || payload?.ehrPatientId || payload?.patientName,
    );
    setLinkedPatient(
      hasPatient
        ? {
            patientId: payload.patientId || null,
            ehrPatientId: payload.ehrPatientId || null,
            patientName: payload.patientName || null,
          }
        : null,
    );
    notifyPhoneCallPatientLink(call.id, {
      patient_id: payload?.patientId || null,
      ehr_patient_id: payload?.ehrPatientId || null,
      patient_name: payload?.patientName || null,
    });
    try {
      await patientService.reassignSessionPatient(call.id, {
        patientId: payload?.patientId || null,
        ehrPatientId: payload?.ehrPatientId || null,
        patientName: payload?.patientName || '',
        reason: 'phone_call_patient_link',
      });
    } catch (err) {
      console.warn(
        '[PhoneCallDetailPanel] persisting call patient link failed',
        err,
      );
    }
    return payload;
  };

  const sessionForPicker = {
    id: call.id,
    patient_id: linkedPatient?.patientId || null,
    ehr_patient_id: linkedPatient?.ehrPatientId || null,
    patient_name: linkedPatient?.patientName || '',
  };

  // Surface the call's identifiers in the Info modal so a telephone encounter
  // can be cross-referenced against the `Conversation` doc (and its transcript
  // subcollection) when debugging. Without this the phone-call detail exposed
  // no id at all.
  const overviewItems = useMemo(
    () =>
      buildSessionOverviewItems({
        session,
        conversationId: call.id,
        messages,
      }),
    [session, call.id, messages],
  );

  // "Company-shared" status pill — same squarer corner radius (md) the recent
  // rows' In-person / Interpreter pills use, via the shared MetaPill.
  const headerExtra = (
    <MetaPill
      gap={1.5}
      px={2.5}
      py={1}
      fontSize="sm"
      borderRadius="md"
      bg={sharedPillBg}
      color={TELEPHONIC_COLOR}
      borderColor={sharedPillBorder}
    >
      <Icon as={TablerPhone} boxSize={3} />
      Company-shared
    </MetaPill>
  );

  // The telephone-encounter affordance lives in the clinical-note slot and
  // mirrors that section exactly (heading + dashed card + "Generate"), just
  // with telephone-encounter copy.
  const bodyLeadingSlot = onCreateTelephoneEncounter ? (
    <VStack as="section" spacing={0} align="stretch" textAlign="left">
      <RecapSectionHeading label="Telephone Encounter" sticky />
      <Flex
        bg={cardBg}
        {...RECAP_CARD_BOUNDARY_PROPS}
        borderColor={borderColor}
        borderStyle="dashed"
        direction={{base: 'column', sm: 'row'}}
        align={{base: 'stretch', sm: 'center'}}
        gap={3}
        px={{base: 3, sm: 4}}
        py={4}
      >
        <VStack align="start" spacing={1} flex={1} minW={0}>
          <Text
            as="h3"
            fontWeight="normal"
            fontSize={PLANNER_SIDEBAR_NAME_FONT_SIZE}
            color={bodyText}
            lineHeight="1.25"
            noOfLines={1}
          >
            Not created yet
          </Text>
        </VStack>
        <Button
          type="button"
          variant="opalitePrimary"
          leftIcon={<TablerBolt size={16} aria-hidden="true" />}
          isLoading={creating}
          loadingText="Generating"
          onClick={handleCreate}
          px={6}
          w={{base: '100%', sm: 'auto'}}
        >
          Generate
        </Button>
      </Flex>
    </VStack>
  ) : null;

  return (
    <>
      <EncounterDetailFrame
        onBack={onBack}
        backLabel="Back to encounters"
        patientName={linkedPatient?.patientName || null}
        onLinkPatient={linkModal.onOpen}
        onChangePatient={linkModal.onOpen}
        onUnlinkPatient={() => setLinkedPatient(null)}
      >
        <SessionDetailView
          session={session}
          messages={messages}
          loadingMessages={loading}
          showNoteTab={false}
          showInfoButton
          showRemoveButton={false}
          extraOverviewItems={overviewItems}
          headerExtra={headerExtra}
          bodyLeadingSlot={bodyLeadingSlot}
          bgColor="gray.50"
          cardBg={cardBg}
          borderColor={borderColor}
          subtleText={subtleText}
          allTemplates={[]}
          containerMinH="100%"
          containerMaxW="100%"
          containerPx={0}
          containerPt={0}
        />
      </EncounterDetailFrame>

      <ChangeSessionPatientModal
        isOpen={linkModal.isOpen}
        onClose={linkModal.onClose}
        session={sessionForPicker}
        onReassign={handleReassignPatient}
        entityLabel="call"
        title="Link patient"
        profilePatientTitle="Link patient"
        profilePatientDescription="Link this phone call to a patient record"
      />
    </>
  );
};

export default PhoneCallDetailPanel;
